import React, { useState, useEffect } from 'react';
import {
    Edit,
    TextInput,
    TextField,
    DateInput,
    TabbedForm,
    FormTab,
    Toolbar,
    SaveButton,
    NumberInput,
    required,
    minValue,
    maxValue,
    FormDataConsumer
} from 'react-admin';
import { change } from 'redux-form';
import { validateRequired, inputStyle } from './UserCreate';
import dataProvider from '../dataProvider';
import SearchableSelect from '../common/SearchableSelect';
import AddressAutoComplete from './AddressAutoComplete';

const UserEditToolbar = props => (
    <Toolbar {...props} >
        <SaveButton />
    </Toolbar>
);

// Custom State Select Component
const StateSelect = ({ record, source, label, validate, style }) => {
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchStates = async () => {
            if (!record || !record.country) {
                setStates([]);
                return;
            }

            setLoading(true);
            try {
                const response = await dataProvider('GET_LIST', 'states', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { country: record.country }
                });
                setStates(response.data);
            } catch (error) {
                console.error('Failed to fetch states:', error);
                setStates([]);
            } finally {
                setLoading(false);
            }
        };

        fetchStates();
    }, [record]);

    return (
        <FormDataConsumer>
            {({ formData, dispatch }) => {
                const value = formData && formData.address && formData.address.state ? formData.address.state : '';
                // Validate prop can be an array or function, handle it safely
                let error = undefined;
                /*if (validate) {
                    if (typeof validate === 'function') {
                        error = validate(value);
                    } else if (Array.isArray(validate) && validate.length > 0) {
                        // If it's an array, run all validators
                        for (const validator of validate) {
                            if (typeof validator === 'function') {
                                const result = validator(value);
                                if (result) {
                                    error = result;
                                    break;
                                }
                            }
                        }
                    }
                }*/
                
                return (
                    <div style={style}>
                        <SearchableSelect
                            value={value}
                            onChange={(e) => {
                                dispatch(change('record-form', source, e.target.value));
                            }}
                            options={states}
                            label={label}
                            disabled={loading}
                            error={!!error}
                            helperText={error}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.name}
                            placeholder="Search state..."
                            allowCustom={false}
                        />
                    </div>
                );
            }}
        </FormDataConsumer>
    );
};

// Custom City Select Component
const CitySelect = ({ record, source, label, validate, style }) => {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedState, setSelectedState] = useState('');

    useEffect(() => {
        const fetchCities = async () => {
            if (!record || !record.country || !selectedState) {
                setCities([]);
                return;
            }

            setLoading(true);
            try {
                const statesResponse = await dataProvider('GET_LIST', 'states', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { country: record.country }
                });
                const state = statesResponse.data.find(s => s.name === selectedState);

                if (state) {
                    const response = await dataProvider('GET_LIST', `cities/state/${state.id}`, {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'name', order: 'ASC' },
                        filter: { country: record.country }
                    });
                    setCities(response.data);
                } else {
                    setCities([]);
                }
            } catch (error) {
                console.error('Failed to fetch cities:', error);
                setCities([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCities();
    }, [selectedState, record]);

    return (
        <FormDataConsumer>
            {({ formData, dispatch }) => {
                const currentState = formData && formData.address && formData.address.state;
                if (currentState !== selectedState) {
                    setSelectedState(currentState);
                }

                const value = formData && formData.address && formData.address.city ? formData.address.city : '';
                // Validate prop can be an array or function, handle it safely
                let error = undefined;
                /*if (validate) {
                    if (typeof validate === 'function') {
                        error = validate(value);
                    } else if (Array.isArray(validate) && validate.length > 0) {
                        // If it's an array, run all validators
                        for (const validator of validate) {
                            if (typeof validator === 'function') {
                                const result = validator(value);
                                if (result) {
                                    error = result;
                                    break;
                                }
                            }
                        }
                    }
                }*/

                return (
                    <div style={style}>
                        <SearchableSelect
                            value={value}
                            onChange={(e) => {
                                dispatch(change('record-form', source, e.target.value));
                            }}
                            options={cities}
                            label={label}
                            disabled={!selectedState || loading}
                            error={!!error}
                            helperText={error}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.name}
                            placeholder="Search city..."
                        />
                    </div>
                );
            }}
        </FormDataConsumer>
    );
};


export const UserEdit = ({ permissions, ...props }) => {
    const CustomPromoterSettingField = () => (
        <FormDataConsumer>
            {({ formData, dispatch, ...rest }) => {
                if (!formData || formData.userType !== 'promoter') {
                    return null;
                }

                // 确保revenueShares存在，如果不存在则初始化
                if (!formData.revenueShares) {
                    formData.revenueShares = [
                        { month: 24, rate: 1.5 },
                        { month: 12, rate: 1 }
                    ];
                }
                const revenueShares = formData.revenueShares;

                const addRevenueShare = () => {
                    const newShares = [...revenueShares, { month: '', rate: '' }];
                    dispatch(change('record-form', 'revenueShares', newShares));
                };

                const removeRevenueShare = (index) => {
                    if (revenueShares.length > 1) {
                        const newShares = revenueShares.filter((_, i) => i !== index);
                        dispatch(change('record-form', 'revenueShares', newShares));
                    }
                };

                return (
                    <>
                        <NumberInput 
                            source="signOnBonusAmount" 
                            label="Sign-on Bonus Amount" 
                            validate={[required(), minValue(0)]} 
                            style={inputStyle}
                        />
                        <div style={{ marginTop: '20px' }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Revenue Share Settings</p>
                            {revenueShares.map((share, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                    <NumberInput
                                        source={`revenueShares[${index}].month`}
                                        label={`Month ${index + 1}`}
                                        validate={[required(), minValue(1)]}
                                        style={{ width: '10em', marginRight: '10px' }}
                                    />
                                    <NumberInput
                                        source={`revenueShares[${index}].rate`}
                                        label={`Rate ${index + 1} (%)`}
                                        validate={[required(), minValue(0), maxValue(100)]}
                                        style={{ width: '10em', marginRight: '10px' }}
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => removeRevenueShare(index)}
                                        style={{ 
                                            padding: '5px 10px', 
                                            backgroundColor: '#f44336', 
                                            color: 'white', 
                                            border: 'none', 
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            marginRight: '5px'
                                        }}
                                        disabled={revenueShares.length <= 1}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button 
                                type="button" 
                                onClick={addRevenueShare}
                                style={{ 
                                    padding: '8px 16px', 
                                    backgroundColor: '#4CAF50', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginTop: '10px'
                                }}
                            >
                                Add Revenue Share
                            </button>
                        </div>
                    </>
                );
            }}
        </FormDataConsumer>
    );

    return (
        <Edit {...props}>
            <TabbedForm toolbar={<UserEditToolbar />}>
                <FormTab label="Base Information">
                    <TextField source="name" label="Username" />
                    <TextInput source="firstName" label="First Name" validate={validateRequired} style={inputStyle} />
                    <TextInput source="lastName" label="Last Name" validate={validateRequired} style={inputStyle} />
                    <TextInput source="middleName" label="Middle Name" style={inputStyle} />
                    <DateInput source="dateOfBirth" label="Date Of Birth" validate={validateRequired} style={inputStyle} />
                    <TextField source="email" label="Email" />
                    <TextInput source="phoneNumber" label="Phone Number" validate={validateRequired} style={inputStyle} />
                    <TextField source="userType" label="User Type" />
                    {(permissions === "system_admin" || permissions === "operator") ? [
                        <CustomPromoterSettingField />
                    ] : null}
                </FormTab>
                <FormTab label="Home Mailing Address">
                    <TextInput source="address.address2" label="Unit Number" style={inputStyle} />
                    <TextInput source="address.address1" label="Street Address" validate={validateRequired} style={inputStyle} />
                    <StateSelect source="address.state" label="State" validate={validateRequired} style={inputStyle} />
                    <CitySelect source="address.city" label="City" validate={validateRequired} style={inputStyle} />
                    <TextInput source="address.postalCode" label="Postal Code" validate={validateRequired} style={inputStyle} />
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};

export default UserEdit;